// src/components/tree/ctl-tree.js
//
// Accessible enterprise tree (navigation + selection).
//
// Attributes (ctl-tree):
// - multiple (boolean)         : allow multi-select (default false)
// - value (string)            : selected id (single mode)
// - values (json array)       : selected ids (multi mode) e.g. '["a","b"]'
// - selectable (boolean)      : allow selection (default true)
// - expand-all (boolean)      : expand all nodes on first render (default false)
// - disabled (boolean)
//
// Properties:
// - items (array)             : [{ id, label, disabled?, expanded?, children?: [...] }]
//
// Events:
// - ctl-select (detail: { id, item, selected, values })
// - ctl-toggle (detail: { id, item, expanded })
//
// Slots:
// - item (template) optional: custom row rendering
//   <template slot="item" data-id="{{id}}" data-depth="{{depth}}">
//     ...use {{label}} {{id}} {{expanded}} {{selected}}...
//   </template>
//
// Keyboard:
// - ArrowUp/ArrowDown: move focus
// - ArrowRight: expand (or move to first child if already expanded)
// - ArrowLeft: collapse (or move to parent if collapsed)
// - Home/End: first/last
// - Enter/Space: select/toggle (selectable) or toggle expand

import "../foundation/ctl-icon.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --ctl-text: var(--ctl-color-text);
    --ctl-muted: var(--ctl-color-muted);
    --ctl-surface: var(--ctl-color-surface);
    --ctl-surface-2: var(--ctl-color-surface-2);
    --ctl-border: var(--ctl-color-border);
    --ctl-brand: var(--ctl-color-brand);
    --ctl-focus: var(--ctl-focus-ring, 0 0 0 3px rgba(79,70,229,.15));

    --ctl-row-radius: var(--ctl-radius-12);
    --ctl-row-pad-y: var(--ctl-space-2);
    --ctl-row-pad-x: var(--ctl-space-2-5);
    --ctl-indent: var(--ctl-space-3-5);

    display:block;
    font-family: var(--ctl-font);
    color: var(--ctl-text);
  }

  .frame{
    border: 1px solid var(--ctl-border);
    background: var(--ctl-surface);
    border-radius: var(--ctl-radius-lg, 14px);
    overflow:hidden;
  }

  .tree{
    list-style:none;
    margin:0;
    padding: var(--ctl-space-1-5);
  }

  .row{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    width: 100%;
    box-sizing: border-box;
    border-radius: var(--ctl-row-radius);
    padding: var(--ctl-row-pad-y) var(--ctl-row-pad-x);
    cursor: pointer;
    user-select:none;
    color: var(--ctl-text);
  }

  .row:hover{
    background: color-mix(in srgb, var(--ctl-brand) 6%, var(--ctl-surface));
  }

  .row:focus-visible{
    outline: none;
    box-shadow: var(--ctl-focus);
  }

  .row[aria-disabled="true"]{
    opacity: var(--ctl-opacity-disabled);
    cursor: not-allowed;
  }

  .row[aria-selected="true"]{
    background: color-mix(in srgb, var(--ctl-brand) 10%, var(--ctl-surface));
  }

  .twisty{
    width: var(--ctl-size-icon-md);
    height: var(--ctl-size-icon-md);
    display:inline-flex;
    align-items:center;
    justify-content:center;
    color: var(--ctl-muted);
    flex: 0 0 auto;
  }

  .twisty svg{
    width: var(--ctl-size-icon-sm);
    height: var(--ctl-size-icon-sm);
    transition: transform var(--ctl-transition-fast);
  }
  .twisty[data-expanded="true"] svg{ transform: rotate(90deg); }

  .label{
    min-width:0;
    flex: 1 1 auto;
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: var(--ctl-font-weight-bold);
    letter-spacing: var(--ctl-tracking-tight);
  }

  .meta{
    color: var(--ctl-muted);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-semibold);
  }

  .spacer{ flex: 0 0 auto; }

  :host([disabled]){
    opacity: var(--ctl-opacity-muted);
    pointer-events:none;
  }

  /* Hide light-dom templates */
  ::slotted(template[slot="item"]){ display:none; }
</style>

<div class="frame" part="frame">
  <ul class="tree" part="tree" role="tree" aria-multiselectable="false"></ul>
  <slot name="item" id="itemSlot"></slot>
</div>
`;

function safeJsonParse(str, fallback) {
  try { return JSON.parse(str); } catch { return fallback; }
}
function toArray(v) { return Array.isArray(v) ? v : []; }
function esc(s){
  return String(s ?? "")
    .replaceAll("&","&amp;").replaceAll("<","&lt;").replaceAll(">","&gt;")
    .replaceAll('"',"&quot;").replaceAll("'","&#039;");
}

function caretSvg(){
  return `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}

export class CtlTree extends HTMLElement {
  static get observedAttributes() {
    return ["multiple","value","values","selectable","expand-all","disabled"];
  }

  constructor(){
    super();
    this.attachShadow({ mode:"open" }).appendChild(template.content.cloneNode(true));

    this._ul = this.shadowRoot.querySelector(".tree");
    this._itemSlot = this.shadowRoot.getElementById("itemSlot");

    this._items = [];
    this._expanded = new Set();
    this._selected = new Set();

    this._flat = []; // visible rows (computed)
    this._focusedId = null;

    this._itemTemplate = null;

    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);
  }

  connectedCallback(){
    if (!this.hasAttribute("selectable")) this.setAttribute("selectable", "");
    this._itemSlot.addEventListener("slotchange", this._onSlotChange);
    this._ul.addEventListener("click", this._onClick);
    this._ul.addEventListener("keydown", this._onKeyDown);

    // hydrate selection from attrs
    if (this.multiple) {
      const vals = toArray(safeJsonParse(this.getAttribute("values") || "[]", []));
      this._selected = new Set(vals.map(String));
    } else {
      const v = this.getAttribute("value");
      this._selected = new Set(v ? [String(v)] : []);
    }

    // expand-all can be applied once items exist
    this._collectItemTemplate();
    this._render();
  }

  disconnectedCallback(){
    this._itemSlot.removeEventListener("slotchange", this._onSlotChange);
    this._ul.removeEventListener("click", this._onClick);
    this._ul.removeEventListener("keydown", this._onKeyDown);
  }

  attributeChangedCallback(){
    // selection hydration
    if (this.multiple) {
      const vals = toArray(safeJsonParse(this.getAttribute("values") || "[]", []));
      this._selected = new Set(vals.map(String));
    } else {
      const v = this.getAttribute("value");
      this._selected = new Set(v ? [String(v)] : []);
    }
    this._render();
  }

  // ---- attributes / properties ----
  get multiple(){ return this.hasAttribute("multiple"); }
  set multiple(v){ v ? this.setAttribute("multiple","") : this.removeAttribute("multiple"); }

  get selectable(){ return this.hasAttribute("selectable"); }
  set selectable(v){ v ? this.setAttribute("selectable","") : this.removeAttribute("selectable"); }

  get disabled(){ return this.hasAttribute("disabled"); }
  set disabled(v){ v ? this.setAttribute("disabled","") : this.removeAttribute("disabled"); }

  get value(){ return this.getAttribute("value") || ""; }
  set value(v){ v ? this.setAttribute("value", String(v)) : this.removeAttribute("value"); }

  get values(){
    const raw = this.getAttribute("values");
    return raw ? toArray(safeJsonParse(raw, [])).map(String) : [];
  }
  set values(v){
    const arr = toArray(v).map(String);
    this.setAttribute("values", JSON.stringify(arr));
  }

  get items(){ return this._items; }
  set items(v){
    const norm = this._normalizeItems(v);
    this._items = norm;

    if (this.hasAttribute("expand-all")) {
      this._expanded = new Set();
      const walk = (nodes) => {
        for (const n of nodes) {
          if (n.children?.length) this._expanded.add(n.id);
          walk(n.children || []);
        }
      };
      walk(this._items);
    } else {
      // honor per-item expanded flags on first set
      this._expanded = new Set();
      const walk = (nodes) => {
        for (const n of nodes) {
          if (n.expanded) this._expanded.add(n.id);
          walk(n.children || []);
        }
      };
      walk(this._items);
    }

    // default focus
    if (!this._focusedId) {
      const first = this._firstVisibleId();
      this._focusedId = first;
    }

    this._render();
  }

  _normalizeItems(v){
    const walk = (arr) => toArray(arr).map((x) => {
      const id = String(x?.id ?? "");
      const label = String(x?.label ?? "");
      const disabled = !!x?.disabled;
      const expanded = !!x?.expanded;
      const children = x?.children ? walk(x.children) : [];
      return { id, label, disabled, expanded, children };
    }).filter((n) => n.id);

    return walk(v);
  }

  // ---- templates ----
  _onSlotChange(){
    this._collectItemTemplate();
    this._render();
  }

  _collectItemTemplate(){
    const assigned = this._itemSlot.assignedElements({ flatten:true }) || [];
    this._itemTemplate = assigned.find((el) => el.tagName === "TEMPLATE") || null;
  }

  _renderTemplate(tmpl, data){
    const html = tmpl.innerHTML.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
      const v = data[key];
      return esc(v == null ? "" : String(v));
    });
    return html;
  }

  // ---- render ----
  _computeFlat(){
    const flat = [];
    const visit = (nodes, depth, parentId) => {
      for (const n of nodes) {
        const hasChildren = (n.children || []).length > 0;
        const expanded = hasChildren ? this._expanded.has(n.id) : false;
        flat.push({ ...n, depth, parentId, hasChildren, expanded });

        if (hasChildren && expanded) visit(n.children, depth + 1, n.id);
      }
    };
    visit(this._items, 0, null);
    this._flat = flat;
  }

  _render(){
    this._ul.setAttribute("aria-multiselectable", this.multiple ? "true" : "false");
    this._computeFlat();

    // keep focus id valid
    if (this._flat.length && !this._flat.some((x) => x.id === this._focusedId)) {
      this._focusedId = this._flat[0].id;
    }

    const selectable = this.selectable;

    const rows = this._flat.map((n) => {
      const selected = selectable && this._selected.has(n.id);
      const disabled = !!n.disabled;
      const padLeft = 6 + n.depth * 14;

      const rowAttrs = [
        `class="row"`,
        `role="treeitem"`,
        `data-id="${esc(n.id)}"`,
        `tabindex="${n.id === this._focusedId ? "0" : "-1"}"`,
        `aria-level="${n.depth + 1}"`,
        `aria-selected="${selected ? "true" : "false"}"`,
        `aria-disabled="${disabled ? "true" : "false"}"`,
        n.hasChildren ? `aria-expanded="${n.expanded ? "true" : "false"}"` : ``,
        `style="padding-left:${padLeft}px;"`
      ].filter(Boolean).join(" ");

      const twisty = n.hasChildren
        ? `<span class="twisty" data-role="twisty" data-expanded="${n.expanded ? "true" : "false"}" aria-hidden="true">${caretSvg()}</span>`
        : `<span class="twisty" aria-hidden="true"></span>`;

      const data = {
        id: n.id,
        label: n.label,
        depth: n.depth,
        expanded: n.expanded ? "true" : "false",
        selected: selected ? "true" : "false",
        disabled: disabled ? "true" : "false",
        hasChildren: n.hasChildren ? "true" : "false"
      };

      const content = this._itemTemplate
        ? this._renderTemplate(this._itemTemplate, data)
        : `
          ${twisty}
          <span class="label">${esc(n.label || n.id)}</span>
        `;

      // NOTE: we intentionally avoid nested <ul> for simpler roving focus;
      // aria-level/expanded provides correct semantics.
      return `<li part="item"><div ${rowAttrs}>${content}</div></li>`;
    }).join("");

    this._ul.innerHTML = rows;

    // restore focus if needed
    queueMicrotask(() => {
      const el = this._rowEl(this._focusedId);
      if (el && document.activeElement === this) el.focus();
    });
  }

  _rowEl(id){
    return id ? this.shadowRoot.querySelector(`.row[data-id="${CSS.escape(id)}"]`) : null;
  }

  _firstVisibleId(){
    this._computeFlat();
    return this._flat[0]?.id || null;
  }

  _indexOfFocused(){
    return this._flat.findIndex((x) => x.id === this._focusedId);
  }

  _focusByIndex(i){
    if (i < 0 || i >= this._flat.length) return;
    const id = this._flat[i].id;
    this._focusedId = id;
    const prev = this.shadowRoot.querySelector(`.row[tabindex="0"]`);
    if (prev) prev.tabIndex = -1;
    const next = this._rowEl(id);
    if (next) {
      next.tabIndex = 0;
      next.focus();
    }
  }

  _toggleExpanded(id){
    const node = this._flat.find((x) => x.id === id);
    if (!node || !node.hasChildren) return;

    const expanded = this._expanded.has(id);
    const next = !expanded;
    if (next) this._expanded.add(id);
    else this._expanded.delete(id);

    this._render();
    this.dispatchEvent(new CustomEvent("ctl-toggle", {
      bubbles: true,
      composed: true,
      detail: { id, item: this._findItemById(id), expanded: next }
    }));
  }

  _select(id){
    if (!this.selectable) return;

    const node = this._flat.find((x) => x.id === id);
    if (!node || node.disabled) return;

    let selected = false;

    if (this.multiple) {
      if (this._selected.has(id)) {
        this._selected.delete(id);
        selected = false;
      } else {
        this._selected.add(id);
        selected = true;
      }
      this.values = Array.from(this._selected);
    } else {
      this._selected = new Set([id]);
      this.value = id;
      selected = true;
    }

    this._render();

    this.dispatchEvent(new CustomEvent("ctl-select", {
      bubbles: true,
      composed: true,
      detail: {
        id,
        item: this._findItemById(id),
        selected,
        values: this.multiple ? Array.from(this._selected) : []
      }
    }));
  }

  _findItemById(id){
    const walk = (nodes) => {
      for (const n of nodes) {
        if (n.id === id) return n;
        const hit = walk(n.children || []);
        if (hit) return hit;
      }
      return null;
    };
    return walk(this._items);
  }

  _parentIdOf(id){
    return this._flat.find((x) => x.id === id)?.parentId || null;
  }

  // ---- interactions ----
  _onClick(e){
    const row = e.composedPath().find((n) => n?.classList?.contains?.("row"));
    if (!row) return;

    const id = row.dataset.id;
    if (!id) return;

    // If click on twisty area, toggle expand
    const twisty = e.composedPath().find((n) => n?.dataset?.role === "twisty");
    if (twisty) {
      e.preventDefault();
      this._toggleExpanded(id);
      return;
    }

    // Default click selects (and if non-selectable, toggles if has children)
    if (this.selectable) this._select(id);
    else this._toggleExpanded(id);
  }

  _onKeyDown(e){
    const key = e.key;
    const idx = this._indexOfFocused();
    if (idx < 0) return;

    if (key === "ArrowDown") { e.preventDefault(); this._focusByIndex(Math.min(this._flat.length - 1, idx + 1)); return; }
    if (key === "ArrowUp")   { e.preventDefault(); this._focusByIndex(Math.max(0, idx - 1)); return; }
    if (key === "Home")      { e.preventDefault(); this._focusByIndex(0); return; }
    if (key === "End")       { e.preventDefault(); this._focusByIndex(this._flat.length - 1); return; }

    const cur = this._flat[idx];

    if (key === "ArrowRight") {
      e.preventDefault();
      if (cur.hasChildren && !cur.expanded) {
        this._toggleExpanded(cur.id);
      } else if (cur.hasChildren && cur.expanded) {
        // move to first child (next row)
        this._focusByIndex(Math.min(this._flat.length - 1, idx + 1));
      }
      return;
    }

    if (key === "ArrowLeft") {
      e.preventDefault();
      if (cur.hasChildren && cur.expanded) {
        this._toggleExpanded(cur.id);
      } else {
        const pid = this._parentIdOf(cur.id);
        if (pid) {
          const pIdx = this._flat.findIndex((x) => x.id === pid);
          if (pIdx >= 0) this._focusByIndex(pIdx);
        }
      }
      return;
    }

    if (key === "Enter" || key === " ") {
      e.preventDefault();
      if (this.selectable) this._select(cur.id);
      else this._toggleExpanded(cur.id);
      return;
    }
  }
}

customElements.define("ctl-tree", CtlTree);
